import { VerifiedEmailsResponse } from '@gofranz/common';
import { Form, FormsIntegrationsQueryParams, FormsIntegrationsResponse, IntegrationsApiResponse, IntegrationsQueryParams, NewFormsIntegration, NewFormsRecipient, UpdateForm } from '@gofranz/formshive-common';
import { Title } from '@mantine/core';
import { lazy, Suspense, useMemo } from 'react';
import { API_BASE_URL } from '../../constants';
import { generateCurlFormData, generateCurlJson } from '../../lib/form-spec-to-curl';
import { generateHtmlFromSpecV2, generateLLMPrompt } from '../../lib/form-spec-to-html';
import { parseAndValidateFormSpec } from '../../lib/form-specs';
import {
  exampleCurlForm,
  exampleCurlJson,
  exampleCurlMulitpart,
  exampleFormHtml,
  exampleFormHtmlFileUpload,
  exampleLLMPrompt,
} from '../../lib/forms';
import { useRustyState } from '../../state';
import { EditForm } from './Edit';
import './form.module.scss';
import { IntegrationHelp } from './Integration';

const AccountMessagesStartPage = lazy(() =>
  import('../../pages/Account/Messags/Start.page').then(module => ({
    default: module.AccountMessagesStartPage
  }))
);

export interface FormDetailProps {
  form: Form;
  submitFormCb: (id: string, newForm: UpdateForm) => Promise<void>;
  deleteCb?: (formId: string) => Promise<void>;

  submitFormRecipientCb: (newForm: NewFormsRecipient) => Promise<void>;
  getVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  getFormVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  deleteRecipientCp: (formId: string, recipientId: string) => Promise<void>;

  submitFormIntegrationCb: (newForm: NewFormsIntegration) => Promise<void>;
  getIntegrations: (params: IntegrationsQueryParams) => Promise<IntegrationsApiResponse>;
  getFormIntegrations: (
    id: string,
    query: FormsIntegrationsQueryParams
  ) => Promise<FormsIntegrationsResponse>;
  deleteFormIntegrationCb: (formId: string, integrationId: string) => Promise<void>;
}

export function FormDetail(props: FormDetailProps) {
  // Memoize URLs - only recalculate when form ID changes
  const urls = useMemo(() => ({
    url: `${API_BASE_URL}/digest/${props.form.id}`,
    challengeUrl: `${API_BASE_URL}/forms/${props.form.id}/challenge/altcha`,
    challengeUrlNoTrack: `${API_BASE_URL}/forms/${props.form.id}/challenge/altcha?track=false`,
  }), [props.form.id]);

  // Parse TOML once and memoize - only recompute when specs change
  const parsedSpec = useMemo(() => {
    if (!props.form.specs || props.form.specs.trim() === '' || !props.form.check_specs) {
      return null;
    }
    const result = parseAndValidateFormSpec(props.form.specs);
    return result.isValid && result.formSpec ? result.formSpec : null;
  }, [props.form.specs, props.form.check_specs]);

  // Derive hasFormSpec from parsed spec
  const hasFormSpec = useMemo(() => parsedSpec !== null, [parsedSpec]);

  // Derive hasFileField from parsed spec
  const hasFileField = useMemo(() => {
    if (!parsedSpec?.fields) return true; // Default to true if no spec
    return Object.values(parsedSpec.fields).some((field) => field.field === 'file');
  }, [parsedSpec]);

  // Generate examples once - only regenerate when dependencies change
  const examples = useMemo(() => {
    if (!parsedSpec) {
      return {
        curlExample: exampleCurlJson(urls.url),
        curlMultipartExample: exampleCurlMulitpart(urls.url),
        curlFormExample: exampleCurlForm(urls.url),
        formExample: exampleFormHtml(urls.url, props.form.check_challenge, urls.challengeUrl),
        formExampleFileUpload: exampleFormHtmlFileUpload(urls.url, props.form.check_challenge, urls.challengeUrl),
        llmExample: exampleLLMPrompt(urls.url, props.form.check_challenge, urls.challengeUrl),
      };
    }

    return {
      curlExample: generateCurlJson(parsedSpec, urls.url),
      curlMultipartExample: hasFileField ? generateCurlFormData(parsedSpec, urls.url) : exampleCurlMulitpart(urls.url),
      curlFormExample: generateCurlFormData(parsedSpec, urls.url),
      formExample: generateHtmlFromSpecV2(parsedSpec, urls.url, props.form.check_challenge, urls.challengeUrl),
      formExampleFileUpload: generateHtmlFromSpecV2(parsedSpec, urls.url, props.form.check_challenge, urls.challengeUrl),
      llmExample: generateLLMPrompt(parsedSpec, urls.url, props.form.check_challenge, urls.challengeUrl),
    };
  }, [parsedSpec, urls, props.form.check_challenge, hasFileField]);

  return (
    <>
      <IntegrationHelp
        form={props.form}
        url={urls.url}
        challengeUrl={urls.challengeUrl}
        challengeUrlNoTrack={urls.challengeUrlNoTrack}
        hasFormSpec={hasFormSpec}
        hasFileField={hasFileField}
        formExample={examples.formExample}
        formExampleFileUpload={examples.formExampleFileUpload}
        curlExample={examples.curlExample}
        curlMultipartExample={examples.curlMultipartExample}
        curlFormExample={examples.curlFormExample}
        llmExample={examples.llmExample}
      />
      <EditForm
        form={props.form}
        formSubmitUrl={urls.url}
        formChallengeUrl={urls.challengeUrl}
        formChallengeUrlNoTrack={urls.challengeUrlNoTrack}
        parsedSpec={parsedSpec}
        hasFormSpec={hasFormSpec}
        hasFileField={hasFileField}
        submitFormCb={props.submitFormCb}
        submitFormRecipientCb={props.submitFormRecipientCb}
        getVerifiedEmails={useRustyState.getState().getAndSetVerifiedEmails}
        getFormVerifiedEmails={props.getFormVerifiedEmails}
        deleteRecipientCp={props.deleteRecipientCp}
        submitFormIntegrationCb={props.submitFormIntegrationCb}
        getIntegrations={props.getIntegrations}
        getFormIntegrations={props.getFormIntegrations}
        deleteFormIntegrationCb={props.deleteFormIntegrationCb}
      />
      <Title order={2} mt="xl" mb="xs">
        Messages
      </Title>
      <Suspense fallback={<div>Loading messages...</div>}>
        <AccountMessagesStartPage formId={props.form.id} />
      </Suspense>
    </>
  );
}
